import { Drawer, createDrawer } from '@nexelem/drawer';
import { ProLightEye } from '@nexelem/icons/ProLightEye';
import { ProLightThumbtack } from '@nexelem/icons/ProLightThumbtack';
import { ProSolidEye } from '@nexelem/icons/ProSolidEye';
import { ProSolidThumbtack } from '@nexelem/icons/ProSolidThumbtack';
import { Banner, Button, IconButton } from '@nexelem/components';
import { SortableList, ActionsProps } from '@nexelem/lists';
import { CustomGridColDef, storeColMapper } from '@nexelem/table';
import { useCallback, useMemo } from 'react';
import { useStore } from 'zustand';
import { useCustomTranslation } from '../../../hooks';
import { FilterGridComponentProps } from '../../organisms/FilteredGridPage/types';

type SortColumnDrawerProps = Pick<FilterGridComponentProps, 'store'> & {
  readonly columns?: CustomGridColDef[];
  readonly defaultColumns?: CustomGridColDef[];
};

const SortColumnDrawer = ({ store, columns = [], defaultColumns = [] }: SortColumnDrawerProps) => {
  const { tWrapper } = useCustomTranslation('components.drawers.sortColumns');

  const savedColumns = useStore(store, (state) => state.currentColumnsSettings);
  const saveColumns = useStore(store, (state) => state.saveColumns);
  const currentOrdering = useStore(store, (state) => state.currentOrdering);

  const colDef = useMemo<CustomGridColDef[]>(() => {
    if (!savedColumns || savedColumns.length === 0) {
      return columns;
    }

    return savedColumns
      .map((savedCol) => {
        const gridCol = columns.find((col) => col.field === savedCol.field);
        return gridCol ? { ...gridCol, ...savedCol } : null;
      })
      .filter((col): col is NonNullable<typeof col> => col !== null);
  }, [savedColumns, columns]);

  // Display columns sorted by pinned status (for visual representation only)
  // Store keeps user's custom order, but UI shows pinned columns first
  const displayColumns = useMemo<CustomGridColDef[]>(() => {
    const pinned = colDef.filter((col) => col.pinned);
    const unpinned = colDef.filter((col) => !col.pinned);
    return [...pinned, ...unpinned];
  }, [colDef]);

  const isSortingColumn = useCallback((field: string) => currentOrdering?.column === field, [currentOrdering]);

  const toggleVisibility = useCallback(
    (field: string) => {
      if (currentOrdering?.column === field) {
        return;
      }

      const allColumns = colDef.map((column) =>
        String(column.field) === field ? { ...column, isHidden: !column.isHidden } : column,
      );
      saveColumns(storeColMapper(allColumns));
    },
    [colDef, saveColumns, currentOrdering],
  );

  const togglePinned = useCallback(
    (field: string) => {
      // Update only the pinned flag, preserving the original order in store
      const allColumns = colDef.map((column) =>
        String(column.field) === field ? { ...column, pinned: !column.pinned } : column,
      );

      saveColumns(storeColMapper(allColumns));
    },
    [colDef, saveColumns],
  );

  const getActions = useCallback(
    (item: CustomGridColDef) => {
      const fieldName = String(item.field);

      const ActionButtons = ({ className }: ActionsProps) => (
        <div className="flex">
          <IconButton
            className={className}
            Icon={item.pinned ? ProSolidThumbtack : ProLightThumbtack}
            size="small"
            onClick={() => togglePinned(fieldName)}
          />
          <IconButton
            className={className}
            disabled={isSortingColumn(fieldName)}
            Icon={item.isHidden ? ProLightEye : ProSolidEye}
            size="small"
            onClick={() => toggleVisibility(fieldName)}
          />
        </div>
      );

      ActionButtons.displayName = 'ActionButtons';
      return ActionButtons;
    },
    [togglePinned, toggleVisibility, isSortingColumn],
  );

  const onDragEnd = useCallback(
    (callback: (prev: CustomGridColDef[]) => CustomGridColDef[]) => {
      // User drags in sorted view (displayColumns: [pinned..., unpinned...])
      const reorderedDisplay = callback(displayColumns);

      // Save the reordered display as new base order
      // This means after drag&drop, the visual order becomes the logical order
      saveColumns(storeColMapper(reorderedDisplay));
    },
    [displayColumns, saveColumns],
  );

  const onRestoreDefault = useCallback(() => {
    saveColumns(storeColMapper(defaultColumns));
  }, [defaultColumns, saveColumns]);

  //TODO - get confirmation from Zosia for that mechanism
  const getDescription = useCallback(
    (item: CustomGridColDef) => {
      const headerName = (item.headerName || item.field).toString();
      const isSorting = isSortingColumn(String(item.field));

      if (isSorting && currentOrdering) {
        const arrow = currentOrdering.asc ? '↑' : '↓';
        return `${headerName} ${arrow}`;
      }

      return headerName;
    },
    [isSortingColumn, currentOrdering],
  );

  return (
    <Drawer>
      <Drawer.Header
        className="flex flex-col gap-6 border-b-0"
        title={<span className="text-t24-sb text-text-primary">{tWrapper('header')}</span>}
        onClose={closeSortColumnsDrawer}
      >
        <div className="flex flex-col gap-4 pr-4">
          <span className="text-s16">{tWrapper('description')}</span>
          <Banner emphasis="weak" message={tWrapper('bannerMessage')} type="info" />
          {currentOrdering && <Banner emphasis="weak" message={tWrapper('sortingColumnWarning')} type="warning" />}
        </div>
      </Drawer.Header>
      <Drawer.Content className="pt-0">
        <SortableList<CustomGridColDef>
          getActions={getActions}
          getDescription={getDescription}
          items={(displayColumns || []).filter((_) => _.headerName)}
          scrollAcceleration={10}
          onDragEnd={onDragEnd}
        />
      </Drawer.Content>
      <Drawer.Footer>
        <Button variant="text" onClick={onRestoreDefault}>
          {tWrapper('reset')}
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
};

export const { toggleSortColumnsDrawer, closeSortColumnsDrawer } = createDrawer(SortColumnDrawer, {
  name: 'SortColumnsDrawer',
  width: 420,
  slot: 'right',
  drawerId: () => 'sort-drawer',
});

export default SortColumnDrawer;
